// Закрытие и переоткрытие периода табеля.
//
// Закрытие необратимо по смыслу: после него правки идут только через переоткрытие, а его делает
// администратор. Поэтому подтверждение — модалка, и в ней стоит ИТОГ (сколько людей, сколько
// часов, какой недобор), а не вопрос «вы уверены»: уверенность человека не информация.
//
// Кнопки не сереют без права. Выключенная кнопка не доводит нажатие до обработчика — человек
// жмёт и не получает ничего. Вместо этого нажатие объясняет, к кому идти.

import { useState } from 'react';

import type { PeriodOut } from '@/shared/api/endpoints/timesheet';
import { useAuthStore } from '@/shared/auth/store';
import { formatHours } from '@/shared/hours/format';
import { can } from '@/shared/rbac/permissions';
import type { Period } from '@/shared/time/period';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Modal from '@/shared/ui/modal';
import { TextField } from '@/shared/ui/text-field';

import { useClosePeriod, useReopenPeriod, useSetTimesheetStatus } from '../queries';

const CLOSE_PERMISSION = 'core.timesheet.close';
/** Отметку «на проверке» ставит тот же, кто ведёт табель: это не заморозка месяца. */
const EDIT_PERMISSION = 'core.timesheet.edit';
const REOPEN_PERMISSION = 'core.timesheet.reopen';

export function ClosePeriodBar({
  период,
  siteId,
  месяц,
  строк,
  главная = true,
}: {
  период: PeriodOut;
  siteId: string;
  месяц: Period;
  /** Сколько людей в табеле: правило 4 требует в подтверждении ИТОГ, а не вопрос. */
  строк: number;
  /**
   * Обычное ли это действие на экране прямо сейчас.
   *
   * Ложь на пустом табеле: закрывать месяц, в котором никто не отмечен, — не то, что тут
   * делают обычно, и выделять эту кнопку значило бы предлагать именно её. Кнопка при этом
   * остаётся нажимаемой: закрыть пустой месяц бывает нужно, и запрещать это нечем.
   */
  главная?: boolean;
}) {
  const права = useAuthStore((state) => state.permissions);
  const закрытие = useClosePeriod(siteId, месяц);
  const состояние = useSetTimesheetStatus(siteId, месяц);
  const переоткрытие = useReopenPeriod(siteId, месяц);

  const [открыта, setОткрыта] = useState(false);
  const [основание, setОснование] = useState('');
  const [ошибкаОснования, setОшибкаОснования] = useState<string | undefined>(undefined);
  const [запрет, setЗапрет] = useState<string | null>(null);

  const закрыт = период.status === 'closed';

  const наПроверке = период.status === 'review';

  if (!закрыт) {
    return (
      <div className='ml-auto flex items-center gap-2'>
        {запрет !== null && <span className='text-paragraph-xs text-text-sub-600'>{запрет}</span>}
        {/* «На проверку» отделено от закрытия намеренно. Состояние объявлено в системе с
            первого дня, и до 15.09.2026 его не ставил никто: табельщик не мог сказать «я
            закончил, смотрите» иначе как голосом, а финотдел не отличал недоведённый месяц
            от готового. Правки при нём ещё принимаются — иначе оно ничем не отличалось бы
            от закрытия и отдало бы заморозку месяца тому, кому её не давали. */}
        <Button.Root
          variant='neutral'
          mode={наПроверке ? 'filled' : 'ghost'}
          size='xsmall'
          onClick={() => {
            if (!can(права, EDIT_PERMISSION)) {
              setЗапрет('Отмечает табельщик или финансовый отдел. Попросите их.');
              return;
            }
            setЗапрет(null);
            состояние.mutate({
              status: наПроверке ? 'draft' : 'review',
              version: период.version,
            });
          }}
        >
          {состояние.isPending
            ? 'Отмечаем…'
            : наПроверке
              ? 'Вернуть в работу'
              : 'На проверку'}
        </Button.Root>
        <Button.Root
          variant={главная ? 'primary' : 'neutral'}
          mode='stroke'
          size='xsmall'
          onClick={() => {
            if (!can(права, CLOSE_PERMISSION)) {
              setЗапрет('Закрывает период финансовый отдел или табельщик. Попросите их.');
              return;
            }
            setЗапрет(null);
            setОткрыта(true);
          }}
        >
          Закрыть период
        </Button.Root>

        <Modal.Root open={открыта} onOpenChange={setОткрыта}>
          <Modal.Content className='max-w-md'>
            <Modal.Header
              title={`Закрыть табель за ${период.month.toString().padStart(2, '0')}.${период.year}?`}
            />
            <Modal.Body>
              <div className='flex flex-col gap-2 text-paragraph-sm text-text-sub-600'>
                {/* Итог, а не вопрос «вы уверены»: человек принимает решение по цифрам. */}
                <p>
                  Объект «{период.site_name}», человек в табеле: {строк}.
                </p>
                {период.totals.has_any_shift ? (
                  <p>
                    Вышло {formatHours(период.totals.fact)}
                    {период.totals.norm !== null && <> из {formatHours(период.totals.norm)}</>}
                    {период.totals.shortfall !== null && Number(период.totals.shortfall) > 0 && (
                      <>, не хватает {formatHours(период.totals.shortfall)}</>
                    )}
                    .
                  </p>
                ) : (
                  // «Вышло 0» тут было бы неправдой: часов не ноль, их просто не отмечали.
                  // Закрывать пустой месяц — почти наверняка ошибка, и сказать об этом надо.
                  <p className='text-warning-base'>
                    За этот месяц не отмечено ни одной смены. Закрытый пустой табель означает, что в
                    расчёт по объекту не попадёт ни один час.
                  </p>
                )}
                <p>
                  После закрытия часы этого месяца не правятся ни в табеле, ни через норматив
                  объекта. Переоткрыть может только администратор, и переоткрытие записывается в
                  журнал.
                </p>
                {закрытие.error !== null && <ErrorAlert error={закрытие.error as never} />}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button.Root
                variant='neutral'
                mode='stroke'
                size='small'
                onClick={() => setОткрыта(false)}
              >
                Отмена
              </Button.Root>
              <Button.Root
                variant='primary'
                mode='filled'
                size='small'
                onClick={() =>
                  закрытие.mutate(период.version, { onSuccess: () => setОткрыта(false) })
                }
              >
                {закрытие.isPending ? 'Закрываем…' : 'Закрыть период'}
              </Button.Root>
            </Modal.Footer>
          </Modal.Content>
        </Modal.Root>
      </div>
    );
  }

  return (
    <div className='ml-auto flex flex-wrap items-center gap-2'>
      <Alert.Root variant='lighter' status='information' size='xsmall'>
        <Alert.Icon />
        Закрыт{период.closed_by_name !== null && <> · {период.closed_by_name}</>}
      </Alert.Root>
      {can(права, REOPEN_PERMISSION) ? (
        <>
          <TextField
            id='reopen-basis'
            label='Основание переоткрытия'
            value={основание}
            onChange={setОснование}
            placeholder='табельщик ошибся в часах по двум людям'
            error={ошибкаОснования}
          />
          <Button.Root
            variant='error'
            mode='stroke'
            size='xsmall'
            onClick={() => {
              if (основание.trim().length < 3) {
                setОшибкаОснования(
                  'Основание обязательно: без него в журнале останется отмена без единого слова о том, почему.',
                );
                return;
              }
              setОшибкаОснования(undefined);
              переоткрытие.mutate({ version: период.version, basis: основание.trim() });
            }}
          >
            {переоткрытие.isPending ? 'Переоткрываем…' : 'Переоткрыть'}
          </Button.Root>
        </>
      ) : (
        <span className='text-paragraph-xs text-text-sub-600'>
          Переоткрывает администратор. Скопируйте ссылку на этот табель из адресной строки и
          передайте ему.
        </span>
      )}
      {переоткрытие.error !== null && <ErrorAlert error={переоткрытие.error as never} />}
    </div>
  );
}
